const cyberpunkStyles = {
  // Core cyberpunk color palette
  colors: {
    primary: '#ff00ff', // Bright magenta/purple
    secondary: '#00ffff', // Cyan
    accent: '#ff0080', // Hot pink
    neon: '#bf00ff', // Electric purple
    warning: '#ffff00', // Electric yellow (minimal use)
    dark: '#0a0a0a', // Deep black
    darkGray: '#1a1a1a', // Dark gray
    mediumGray: '#2a2a2a', // Medium gray
    lightGray: '#404040', // Light gray
    white: '#ffffff',
    glitch: '#ff3366', // Glitch red
  },

  // Main container styles
  cyberpunkContainer: {
    background: '#080808',
    minHeight: '100vh',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `
        radial-gradient(circle at 20% 80%, rgba(255, 0, 255, 0.1) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(0, 255, 255, 0.1) 0%, transparent 50%),
        radial-gradient(circle at 40% 40%, rgba(191, 0, 255, 0.05) 0%, transparent 50%)
      `,
      pointerEvents: 'none',
    },
  },

  // Neon letter flicker styles for hero title
  cyberpunkLetterFlicker: `
    @keyframes neonFlickerOnce {
      0%, 40% {
        color: rgba(80, 70, 60, 0.3);
        text-shadow: none;
        filter: brightness(0.25) contrast(0.6) sepia(0.2);
        -webkit-text-stroke: 3px rgba(60, 55, 50, 0.25);
      }
      50% {
        color: #00ffff;
        text-shadow: 
          0 0 8px rgba(0, 255, 255, 0.9),
          0 0 16px rgba(0, 255, 255, 0.6),
          0 0 24px rgba(0, 255, 255, 0.4),
          0 0 32px rgba(0, 255, 255, 0.3);
        filter: brightness(1.2);
        -webkit-text-stroke: 3px rgba(0, 200, 200, 0.4);
      }
      60%, 100% {
        color: #00ffff;
        text-shadow: 
          0 0 6px rgba(0, 255, 255, 0.8),
          0 0 12px rgba(0, 255, 255, 0.6),
          0 0 18px rgba(0, 255, 255, 0.4),
          0 0 24px rgba(0, 255, 255, 0.3),
          0 0 36px rgba(0, 255, 255, 0.2);
        filter: brightness(1);
        -webkit-text-stroke: 3px rgba(0, 200, 200, 0.4);
      }
    }
    
    @keyframes flicker {
      0%, 19.999%, 22%, 62.999%, 64%, 64.999%, 70%, 100% {
        opacity: 1;
      }
      20%, 21.999%, 63%, 63.999%, 65%, 69.999% {
        opacity: 0.4;
      }
    }
    
    .neon-letter {
      color: rgba(80, 70, 60, 0.3);
      text-shadow: none;
      filter: brightness(0.10) contrast(0.6) sepia(0.2);
      -webkit-text-stroke: 3px rgba(60, 55, 50, 0.25);
      position: relative;
      font-weight: 400;
      font-size: 6rem;
    }
    
    .neon-letter::before {
      content: attr(data-char);
      position: absolute;
      top: 0;
      left: 0;
      color: rgba(90, 85, 80, 0.15);
      text-shadow: 
        inset 0 0 3px rgba(70, 65, 60, 0.2),
        0 1px 2px rgba(0, 0, 0, 0.3);
      -webkit-text-stroke: 4px rgba(80, 75, 70, 0.18);
      z-index: -1;
      filter: blur(0.5px);
    }
  `,

  // Hero section styles
  heroSection: {
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    background: 'transparent',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `
        repeating-linear-gradient(
          90deg,
          transparent,
          transparent 2px,
          rgba(255, 0, 255, 0.03) 2px,
          rgba(255, 0, 255, 0.03) 4px
        )
      `,
      pointerEvents: 'none',
      animation: '$scanlines 2s linear infinite',
    },
  },

  // Typography styles
  cyberpunkTitle: {
    fontSize: '4.5rem',
    fontWeight: 900,
    fontFamily: '"Orbitron", "Roboto", monospace',
    background: 'linear-gradient(45deg, #ff00ff, #00ffff, #ff00ff)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    textShadow: `
      0 0 10px rgba(255, 0, 255, 0.5),
      0 0 20px rgba(255, 0, 255, 0.3),
      0 0 30px rgba(255, 0, 255, 0.2)
    `,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    position: 'relative',
    animation: '$glitch 3s infinite',
    '@media (max-width: 768px)': {
      fontSize: '2.5rem',
    },
  },

  cyberpunkSubtitle: {
    fontSize: '1.5rem',
    fontFamily: '"Share Tech Mono", monospace',
    color: '#00ffff',
    textShadow: '0 0 10px rgba(0, 255, 255, 0.5)',
    letterSpacing: '0.05em',
    marginTop: '1rem',
    opacity: 0.9,
    '@media (max-width: 768px)': {
      fontSize: '1.1rem',
    },
  },

  // Button styles
  cyberpunkButton: {
    background: 'linear-gradient(45deg, transparent, rgba(255, 0, 255, 0.1), transparent)',
    border: '2px solid #ff00ff',
    color: '#ff00ff',
    padding: '12px 30px',
    fontSize: '1rem',
    fontFamily: '"Orbitron", monospace',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255, 0, 255, 0.2), transparent)',
      transition: 'left 0.5s ease',
    },
    '&:hover': {
      boxShadow: `
        0 0 20px rgba(255, 0, 255, 0.5),
        inset 0 0 20px rgba(255, 0, 255, 0.1)
      `,
      transform: 'translateY(-2px)',
      '&::before': {
        left: '100%',
      },
    },
  },

  // About section styles
  aboutSection: {
    padding: '100px 0',
    background: 'rgba(12, 12, 12, 0.9)',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `
        linear-gradient(45deg, transparent 49%, rgba(255, 0, 255, 0.1) 50%, transparent 51%),
        linear-gradient(-45deg, transparent 49%, rgba(0, 255, 255, 0.1) 50%, transparent 51%)
      `,
      backgroundSize: '20px 20px',
      opacity: 0.3,
      animation: '$matrix 20s linear infinite',
    },
  },

  aboutTitle: {
    fontSize: '3rem',
    fontFamily: '"Orbitron", monospace',
    fontWeight: 700,
    color: '#ff00ff',
    textAlign: 'center',
    marginBottom: '2rem',
    textShadow: '0 0 15px rgba(255, 0, 255, 0.5)',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },

  aboutText: {
    fontSize: '1.2rem',
    fontFamily: '"Share Tech Mono", monospace',
    color: '#00ffff',
    maxWidth: '800px',
    margin: '0 auto',
    textAlign: 'center',
    lineHeight: 1.8,
    textShadow: '0 0 5px rgba(0, 255, 255, 0.3)',
  },

  // Animation keyframes
  '@keyframes glitch': {
    '0%, 100%': {
      transform: 'translate(0)',
    },
    '20%': {
      transform: 'translate(-2px, 2px)',
    },
    '40%': {
      transform: 'translate(-2px, -2px)',
    },
    '60%': {
      transform: 'translate(2px, 2px)',
    },
    '80%': {
      transform: 'translate(2px, -2px)',
    },
  },

  '@keyframes scanlines': {
    '0%': {
      transform: 'translateY(-100%)',
    },
    '100%': {
      transform: 'translateY(100vh)',
    },
  },

  '@keyframes matrix': {
    '0%': {
      transform: 'translateY(0) translateX(0)',
    },
    '100%': {
      transform: 'translateY(-20px) translateX(-20px)',
    },
  },

  '@keyframes neonPulse': {
    '0%, 100%': {
      opacity: 1,
      filter: 'brightness(1)',
    },
    '50%': {
      opacity: 0.8,
      filter: 'brightness(1.2)',
    },
  },

  '@keyframes staticNoise': {
    '0%': {
      transform: 'translateX(0px)',
    },
    '10%': {
      transform: 'translateX(-1px)',
    },
    '20%': {
      transform: 'translateX(1px)',
    },
    '30%': {
      transform: 'translateX(-1px)',
    },
    '40%': {
      transform: 'translateX(1px)',
    },
    '50%': {
      transform: 'translateX(0px)',
    },
    '60%': {
      transform: 'translateX(1px)',
    },
    '70%': {
      transform: 'translateX(-1px)',
    },
    '80%': {
      transform: 'translateX(1px)',
    },
    '90%': {
      transform: 'translateX(-1px)',
    },
    '100%': {
      transform: 'translateX(0px)',
    },
  },

  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 0 20px rgba(0, 255, 255, 0.5)',
      opacity: 1,
    },
    '50%': {
      boxShadow: '0 0 30px rgba(0, 255, 255, 0.8), 0 0 40px rgba(0, 255, 255, 0.3)',
      opacity: 0.9,
    },
    '100%': {
      boxShadow: '0 0 20px rgba(0, 255, 255, 0.5)',
      opacity: 1,
    },
  },

  '@keyframes cyberpunkPulse': {
    '0%': {
      boxShadow: '0 0 20px rgba(0, 255, 255, 0.6), inset 0 0 10px rgba(0, 0, 0, 0.8)',
      textShadow: '0 0 8px #00ffff, 0 0 16px #00ffff, 0 0 24px #00ffff',
    },
    '50%': {
      boxShadow: '0 0 30px rgba(0, 255, 255, 0.9), 0 0 40px rgba(0, 255, 255, 0.4), inset 0 0 15px rgba(0, 0, 0, 0.9)',
      textShadow: '0 0 12px #00ffff, 0 0 24px #00ffff, 0 0 36px #00ffff',
    },
    '100%': {
      boxShadow: '0 0 20px rgba(0, 255, 255, 0.6), inset 0 0 10px rgba(0, 0, 0, 0.8)',
      textShadow: '0 0 8px #00ffff, 0 0 16px #00ffff, 0 0 24px #00ffff',
    },
  },

  '@keyframes cyberpunkPulseMagenta': {
    '0%': {
      boxShadow: '0 0 20px rgba(255, 0, 255, 0.6), inset 0 0 10px rgba(0, 0, 0, 0.8)',
      textShadow: '0 0 8px #ff00ff, 0 0 16px #ff00ff, 0 0 24px #ff00ff',
    },
    '50%': {
      boxShadow: '0 0 30px rgba(255, 0, 255, 0.9), 0 0 40px rgba(255, 0, 255, 0.4), inset 0 0 15px rgba(0, 0, 0, 0.9)',
      textShadow: '0 0 12px #ff00ff, 0 0 24px #ff00ff, 0 0 36px #ff00ff',
    },
    '100%': {
      boxShadow: '0 0 20px rgba(255, 0, 255, 0.6), inset 0 0 10px rgba(0, 0, 0, 0.8)',
      textShadow: '0 0 8px #ff00ff, 0 0 16px #ff00ff, 0 0 24px #ff00ff',
    },
  },

  // Glitch overlay
  glitchOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      repeating-linear-gradient(
        0deg,
        rgba(255, 51, 102, 0.03),
        rgba(255, 51, 102, 0.03) 1px,
        transparent 1px,
        transparent 2px
      )
    `,
    pointerEvents: 'none',
    animation: '$staticNoise 0.2s infinite',
  },

  // Flowing background animation
  flowingBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 10% 20%, rgba(255, 0, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 90% 80%, rgba(0, 255, 255, 0.1) 0%, transparent 50%)
    `,
    animation: '$neonPulse 4s ease-in-out infinite',
    pointerEvents: 'none',
  },
}

export default cyberpunkStyles
